import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, FileText, AlertTriangle, X, Loader2, Inbox } from 'lucide-react';
import { usePageHeader } from '../components/Layout';
import { SectionCard, Chip } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ENTITY_LABEL = {
  project: 'Proje',
  team: 'Takım',
  workflow: 'İş Akışı',
  task: 'Görev',
};

export default function ApprovalRequests() {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);

  usePageHeader([{ label: 'Bekleyen Onaylar' }], null, []);

  useEffect(() => {
    if (user) fetchApprovalRequests();
  }, [user]);

  const fetchApprovalRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/approval-requests`, { params: { status: 'pending' } });
      setRequests(response.data);
    } catch {
      toast.error('Onay istekleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      setProcessing(true);
      await axios.patch(`${API}/approval-requests/${id}/approve`);
      toast.success('İstek onaylandı ve işlem gerçekleştirildi');
      setSelected(null);
      fetchApprovalRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onaylama başarısız');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (id) => {
    try {
      setProcessing(true);
      await axios.patch(`${API}/approval-requests/${id}/reject`);
      toast.success('İstek reddedildi');
      setSelected(null);
      fetchApprovalRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reddetme başarısız');
    } finally {
      setProcessing(false);
    }
  };

  const getTypeLabel = (type) => (type === 'edit' ? 'Düzenleme' : 'Silme');
  const getEntityLabel = (entity) => ENTITY_LABEL[entity] || entity;

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('tr-TR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <SectionCard
        title={`Bekleyen İstekler (${requests.length})`}
        subtitle="Düzenleme ve silme işlemleri için bekleyen onay istekleri"
        pad={false}
      >
        {requests.length === 0 ? (
          <div className="py-16 text-center">
            <Inbox size={36} className="mx-auto mb-3 opacity-30" />
            <div className="text-sm font-medium">Bekleyen onay isteği bulunmuyor</div>
            <div className="text-xs ox-text-muted mt-1">Yeni onay istekleri burada görünecektir</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {requests.map((r) => {
              const isDelete = r.type === 'delete';
              return (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="ox-surface rounded-md p-4 text-left hover:border-[color:var(--accent)] transition-colors"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className="flex items-center justify-center rounded-md flex-shrink-0"
                        style={{
                          width: 32,
                          height: 32,
                          background: isDelete ? 'var(--danger-weak)' : 'var(--accent-weak)',
                          color: isDelete ? 'var(--danger)' : 'var(--accent)',
                        }}
                      >
                        {isDelete ? <XCircle size={16} /> : <FileText size={16} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{r.entityName}</div>
                        <div className="text-[11px] ox-text-muted">{getEntityLabel(r.entity)}</div>
                      </div>
                    </div>
                    <Chip
                      color={isDelete ? 'var(--danger)' : 'var(--accent)'}
                      bg={isDelete ? 'var(--danger-weak)' : 'var(--accent-weak)'}
                    >
                      {getTypeLabel(r.type)}
                    </Chip>
                  </div>
                  <div className="space-y-1.5 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="text-[12px] ox-text-muted flex items-center gap-2">
                      <span className="ox-text-dim">İsteyen:</span>
                      <span className="truncate">{r.requestedByName}</span>
                    </div>
                    <div className="text-[11.5px] ox-text-dim flex items-center gap-1.5">
                      <Clock size={11} />
                      {formatDate(r.createdAt)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>

      {selected && (
        <div className="ox-modal-backdrop" onClick={() => !processing && setSelected(null)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="flex items-center gap-2">
                {selected.type === 'delete' ? (
                  <XCircle size={16} style={{ color: 'var(--danger)' }} />
                ) : (
                  <FileText size={16} style={{ color: 'var(--accent)' }} />
                )}
                <div>
                  <div className="text-sm font-semibold">{selected.entityName}</div>
                  <div className="text-[11px] ox-text-muted">
                    {getEntityLabel(selected.entity)} — {getTypeLabel(selected.type)}
                  </div>
                </div>
              </div>
              <button className="ox-btn ox-btn-ghost" onClick={() => !processing && setSelected(null)}>
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body space-y-4">
              <div className="grid grid-cols-2 gap-3 ox-surface-muted rounded-md p-3">
                <DetailField label="İsteyen" value={selected.requestedByName} />
                <DetailField label="İstek Tarihi" value={formatDate(selected.createdAt)} />
                <DetailField label="İşlem Tipi" value={getTypeLabel(selected.type)} />
                <DetailField label="Entity Tipi" value={getEntityLabel(selected.entity)} />
              </div>

              {selected.type === 'edit' && selected.requestDetails && (
                <div className="ox-surface-muted rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-wider ox-text-muted mb-2 flex items-center gap-1.5">
                    <AlertTriangle size={12} />
                    Değiştirilecek Alanlar
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(selected.requestDetails).map(([key, value]) => (
                      <div key={key} className="text-[13px] flex gap-2">
                        <span className="ox-text-muted">{key}:</span>
                        <span className="font-medium break-all">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.type === 'delete' && (
                <div className="rounded-md p-3 flex items-start gap-2" style={{ background: 'var(--danger-weak)', color: 'var(--danger)' }}>
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <div className="text-[13px]">
                    <strong>Uyarı:</strong> Bu işlem geri alınamaz. {getEntityLabel(selected.entity)} kalıcı olarak silinecektir.
                  </div>
                </div>
              )}
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn"
                onClick={() => handleReject(selected.id)}
                disabled={processing}
                style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
              >
                <XCircle size={13} style={{ marginRight: 6 }} />
                {processing ? 'İşleniyor...' : 'Reddet'}
              </button>
              <button
                className="ox-btn ox-btn-primary"
                onClick={() => handleApprove(selected.id)}
                disabled={processing}
              >
                <CheckCircle2 size={13} style={{ marginRight: 6 }} />
                {processing ? 'İşleniyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider ox-text-dim">{label}</div>
      <div className="text-sm font-medium mt-0.5 break-words">{value}</div>
    </div>
  );
}
